import { Sheet } from "@/components/shell/Sheet";
import {
  CHANGELOG,
  type ChangelogCategory,
  type ChangelogEntry,
  type ChangelogSection,
} from "@/lib/changelog";
import { cn } from "@/lib/utils/cn";

type Props = {
  open: boolean;
  onClose: () => void;
};

const SECTION_LABEL: Record<ChangelogCategory, string> = {
  feature: "✨ Nowe funkcje",
  bugfix: "🔧 Poprawki błędów",
  security: "🔒 Poprawki bezpieczeństwa",
};

const SECTION_PILL: Record<ChangelogCategory, string> = {
  feature: "bg-purple/15 text-purple",
  bugfix: "bg-sunny/30 text-pending",
  security: "bg-line text-muted",
};

const SECTION_BULLET: Record<ChangelogCategory, string> = {
  feature: "✨",
  bugfix: "🔧",
  security: "🔒",
};

/**
 * Historia wersji — bottom sheet otwierany z Settings (klik w numer wersji).
 *
 * Wpisy w `lib/changelog.ts`. Layout: na samej górze numer + data wersji,
 * pod spodem sekcje (feature / bugfix / security) z mini-nagłówkami,
 * rozdzielone subtelnym separatorem gdy w jednej wersji jest więcej niż
 * jedna sekcja.
 */
export function ChangelogSheet({ open, onClose }: Props) {
  return (
    <Sheet open={open} onClose={onClose} title="📓 Historia wersji">
      <p className="-mt-1 mb-3 text-[11px] font-semibold tracking-[0.08em] text-muted uppercase">
        Co się zmieniło
      </p>

      <div className="space-y-5">
        {CHANGELOG.map((entry, i) => (
          <Entry key={i} entry={entry} />
        ))}
      </div>
    </Sheet>
  );
}

export function Entry({ entry }: { entry: ChangelogEntry }) {
  return (
    <section className="space-y-3">
      <header className="flex items-baseline gap-2">
        <span className="font-num text-[15px] font-bold text-ink">
          v{entry.version}
        </span>
        <span className="font-num text-[11px] text-muted">{entry.date}</span>
      </header>

      <div className="space-y-3">
        {entry.sections.map((section, i) => (
          <div key={i}>
            {i > 0 && (
              <div
                aria-hidden
                className="h-px bg-line my-3"
              />
            )}
            <SectionBlock section={section} />
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionBlock({ section }: { section: ChangelogSection }) {
  return (
    <>
      <div className="mb-2">
        <span
          className={cn(
            "inline-flex rounded-full px-2.5 py-1 text-[10px] font-extrabold tracking-wide uppercase",
            SECTION_PILL[section.kind],
          )}
        >
          {SECTION_LABEL[section.kind]}
        </span>
      </div>
      <ul className="space-y-1.5 text-[13px] leading-snug text-ink">
        {section.kind === "security" ? (
          <li className="flex gap-2">
            <span aria-hidden>{SECTION_BULLET.security}</span>
            <span className="text-muted">
              Szczegóły nieujawnione — zalecane odświeżenie aplikacji.
            </span>
          </li>
        ) : (
          section.items?.map((item, i) => (
            <li key={i} className="flex gap-2">
              <span aria-hidden>{SECTION_BULLET[section.kind]}</span>
              <span>{item}</span>
            </li>
          ))
        )}
      </ul>
    </>
  );
}
